import { useState } from "react";
import { ChevronDown, ChevronRight, Gauge, Activity, Fuel, Thermometer } from "lucide-react";

const AESfocRpm = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselAuxEngineData",
      fields: [
        "vesselId", "voyageId", "engineId", "recordedAt", "engineType",
        "fuelID", "fuelType", "sulphurContent", "fuelConsumption",
        "powerOutput", "maxCapacityLoad", "reportedCapacityLoad", 
        "averageReportedLoad", "rpm", "powerLoad", "sfoc", "status"
      ],
      purpose: "Auxiliary engine operational data"
    },
    {
      collection: "Vessel",
      fields: ["_id", "name", "imoNumber"],
      purpose: "Vessel identification and metadata"
    },
    {
      collection: "VesselVoyage",
      fields: ["voyageId", "departTime", "arrivalTime", "distanceTravelled"],
      purpose: "Voyage information for context"
    }
  ];

  const coreCalculations = [
    {
      id: "mcr-load-calculation",
      title: "MCR Load Percentage",
      description: "Calculates engine load relative to maximum continuous rating",
      formula: "MCR Load % = (Power Output ÷ Max Capacity) × 100%",
      subFormulas: [
        "powerLoad: Stored directly from AE_load field",
        "maxCapacityLoad: From AE_Max_Capacity field",
        "reportedCapacityLoad: From AE_Reported_Capacity_Load field",
        "averageReportedLoad: From AE_Average_Reported_Load field"
      ],
      icon: <Gauge className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "sfoc-calculation",
      title: "SFOC (Specific Fuel Oil Consumption)",
      description: "Fuel efficiency measurement in grams per kilowatt-hour",
      formula: "SFOC (g/kWh) = Fuel Consumption ÷ Power Output × 1000",
      subFormulas: [
        "Direct from AE_SFOC field",
        "Calculated from fuel consumption and power output",
        "Standard unit: grams per kilowatt-hour",
        "Lower SFOC = Better efficiency"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "rpm-monitoring",
      title: "RPM (Revolutions Per Minute)",
      description: "Engine rotational speed monitoring",
      formula: "RPM = Direct measurement from AE_RPM field",
      subFormulas: [
        "Direct from AE_RPM field",
        "Typically 700-900 RPM for auxiliary engines",
        "Correlates with load percentage",
        "Optimal range for efficiency"
      ],
      icon: <Activity className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "fuel-consumption",
      title: "Fuel Consumption Breakdown",
      description: "Detailed fuel consumption by type",
      formula: "Total Fuel = HFO + LFO + MGO + Others",
      subFormulas: [
        "HFO: AE_Heavy_Fuel_Oil field",
        "LFO: AE_Light_Fuel_Oil field",
        "MGO: AE_Marine_Gas_Oil field",
        "Others: AE_Others field",
        "Total: Sum of all fuel types"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    }
  ];

  const dataFields = [
    { field: "AE_load", type: "Pipe-separated", unit: "%", description: "MCR load percentage per engine" },
    { field: "AE_SFOC", type: "Pipe-separated", unit: "g/kWh", description: "Specific fuel oil consumption" },
    { field: "AE_RPM", type: "Pipe-separated", unit: "RPM", description: "Engine rotational speed" },
    { field: "AE_Power", type: "Pipe-separated", unit: "kW", description: "Power output" },
    { field: "AE_Max_Capacity", type: "Pipe-separated", unit: "kW", description: "Maximum continuous rating" },
    { field: "AE_Reported_Capacity_Load", type: "Pipe-separated", unit: "%", description: "Reported load capacity" },
    { field: "AE_Average_Reported_Load", type: "Pipe-separated", unit: "%", description: "Average reported load" },
    { field: "AE_Heavy_Fuel_Oil", type: "Pipe-separated", unit: "MT", description: "Heavy fuel oil consumption" },
    { field: "AE_Light_Fuel_Oil", type: "Pipe-separated", unit: "MT", description: "Light fuel oil consumption" },
    { field: "AE_Marine_Gas_Oil", type: "Pipe-separated", unit: "MT", description: "Marine gas oil consumption" },
    { field: "AE_Others", type: "Pipe-separated", unit: "MT", description: "Other fuel consumption" },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fad8d4] rounded-lg">
              <Gauge className="w-6 h-6 text-[#a93226]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Auxiliary Engine MCR Load Analysis
              </h2>
              <p className="text-gray-600 text-sm">
                SFOC and RPM analysis relative to MCR Load percentage with comprehensive data processing
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-800">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations & Metrics</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Field Specifications */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Field Specifications</h3>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white rounded border">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Field Name</th>
                        <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Type</th>
                        <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Unit</th>
                        <th className="py-2 px-4 border text-left text-sm font-medium text-gray-700">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dataFields.map((field, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="py-2 px-4 border font-mono text-sm">{field.field}</td>
                          <td className="py-2 px-4 border text-sm">{field.type}</td>
                          <td className="py-2 px-4 border text-sm font-mono">{field.unit}</td>
                          <td className="py-2 px-4 border text-sm">{field.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AESfocRpm;